"use client"

import { useState, useEffect, useCallback } from "react"
import { Plus, Search, SlidersHorizontal, Briefcase, Globe } from "lucide-react"
import ProjectCard from "./ProjectCard"
import CreateProjectModal from "./CreateProjectModal"

interface Project {
  id: string
  title: string
  description: string
  location: string
  category: string
  budget?: string | null
  timeline?: string | null
  urgency: "flexible" | "normal" | "urgent"
  tags: string[]
  status: "open" | "awaiting_scope" | "in_progress" | "completed" | "cancelled"
  createdAt: string
  postedBy: { id: string; name: string; userType: string; profileImage?: string | null }
  _count: { bids: number }
}

interface Props {
  initialProjects: Project[]
  currentUserId?: string
  userType?: string | null
  userLocation?: string | null
}

const CATEGORIES = [
  "All", "Plumbing", "Electrical", "Carpentry", "Painting", "Roofing", "HVAC",
  "Landscaping", "Concrete", "Flooring", "Tiling", "Drywall", "Masonry",
  "General Contracting", "Renovation", "New Construction", "Other",
]

type Tab = "browse" | "mine"

export default function ProjectsBrowseClient({ initialProjects, currentUserId, userType, userLocation }: Props) {
  const [tab, setTab] = useState<Tab>("mine")
  const [projects, setProjects] = useState<Project[]>(initialProjects)
  const [myProjects, setMyProjects] = useState<Project[]>([])
  const [myBidProjects, setMyBidProjects] = useState<Project[]>([])
  const [showModal, setShowModal] = useState(false)
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("All")
  const [urgency, setUrgency] = useState("all")
  const [loading, setLoading] = useState(false)
  const [myLoading, setMyLoading] = useState(false)

  const canPost = userType === "homeowner" || userType === "company"
  const canBid = userType === "tradesperson" || userType === "company"

  const fetchMyActivity = useCallback(async () => {
    if (!currentUserId) return
    setMyLoading(true)

    const [postedRes, bidsRes] = await Promise.all([
      fetch("/api/projects?mine=1&status=all"),
      fetch("/api/projects?myBids=1&status=all"),
    ])

    if (postedRes.ok) {
      const d = await postedRes.json()
      setMyProjects(d.projects ?? [])
    }
    if (bidsRes.ok) {
      const d = await bidsRes.json()
      // Exclude projects the user posted (already in myProjects)
      setMyBidProjects((d.projects ?? []).filter((p: Project) => p.postedBy.id !== currentUserId))
    }
    setMyLoading(false)
  }, [currentUserId])

  useEffect(() => {
    if (tab === "mine") fetchMyActivity()
  }, [tab, fetchMyActivity])

  async function applyFilters(newCategory?: string, newUrgency?: string) {
    const cat = newCategory ?? category
    const urg = newUrgency ?? urgency
    setLoading(true)
    const params = new URLSearchParams({ status: "open" })
    if (cat !== "All") params.set("category", cat)
    if (urg !== "all") params.set("urgency", urg)
    if (search.trim()) params.set("location", search.trim())
    const res = await fetch(`/api/projects?${params}`)
    if (res.ok) {
      const data = await res.json()
      setProjects(data.projects)
    }
    setLoading(false)
  }

  function handleCreated(project: unknown) {
    setProjects((prev) => [project as Project, ...prev])
    if (tab === "mine") setMyProjects((prev) => [project as Project, ...prev])
  }

  const totalMyActivity = myProjects.length + myBidProjects.length

  return (
    <main className="max-w-5xl mx-auto px-4 py-6 pb-24">
      {/* Page header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {tab === "browse" ? "Browse open projects and submit your bid" : "Your posted projects and active bids"}
          </p>
        </div>
        {canPost && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Post a Project
          </button>
        )}
      </div>

      {/* Tab bar */}
      {currentUserId && (
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit">
          <button
            onClick={() => setTab("browse")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === "browse" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Globe className="w-4 h-4" />
            Browse
          </button>
          <button
            onClick={() => setTab("mine")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === "mine" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Briefcase className="w-4 h-4" />
            My Activity
            {totalMyActivity > 0 && tab !== "mine" && (
              <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center leading-none">
                {totalMyActivity}
              </span>
            )}
          </button>
        </div>
      )}

      {/* ── BROWSE TAB ── */}
      {tab === "browse" && (
        <>
          {/* Search + filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-5 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="Search by location…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <SlidersHorizontal className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.slice(0, 8).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { setCategory(cat); applyFilters(cat, urgency) }}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      category === cat
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 ml-auto">
                {["all", "urgent", "normal", "flexible"].map((u) => (
                  <button
                    key={u}
                    onClick={() => { setUrgency(u); applyFilters(category, u) }}
                    className={`text-xs px-3 py-1 rounded-full border capitalize transition-colors ${
                      urgency === u
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {u === "all" ? "All urgency" : u}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium mb-1">No projects found</p>
              <p className="text-sm">Try adjusting your filters{canPost ? " or post the first one" : ""}.</p>
              {canPost && (
                <button onClick={() => setShowModal(true)} className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                  Post a Project
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {projects.map((p) => <ProjectCard key={p.id} project={p} />)}
            </div>
          )}
        </>
      )}

      {/* ── MY ACTIVITY TAB ── */}
      {tab === "mine" && (
        <div className="space-y-6">
          {myLoading ? (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          ) : (
            <>
              {/* Projects I posted */}
              {canPost && (
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Projects I Posted ({myProjects.length})
                  </h2>
                  {myProjects.length === 0 ? (
                    <div className="text-center py-8 bg-white border border-dashed border-gray-200 rounded-xl text-gray-400 text-sm">
                      <p>You haven&apos;t posted any projects yet.</p>
                      <button onClick={() => setShowModal(true)} className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                        Post a Project
                      </button>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2">
                      {myProjects.map((p) => <ProjectCard key={p.id} project={p} showStatus />)}
                    </div>
                  )}
                </section>
              )}

              {/* Projects I bid on */}
              {canBid && (
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Projects I Bid On ({myBidProjects.length})
                  </h2>
                  {myBidProjects.length === 0 ? (
                    <div className="text-center py-8 bg-white border border-dashed border-gray-200 rounded-xl text-gray-400 text-sm">
                      <p>You haven&apos;t bid on any projects yet.</p>
                      <button onClick={() => setTab("browse")} className="mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                        Browse Open Projects
                      </button>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-2">
                      {myBidProjects.map((p) => <ProjectCard key={p.id} project={p} showStatus />)}
                    </div>
                  )}
                </section>
              )}

              {/* Company — show both sections */}
              {!canPost && !canBid && (
                <div className="text-center py-12 text-gray-400">No activity yet.</div>
              )}
            </>
          )}
        </div>
      )}

      {showModal && (
        <CreateProjectModal
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
          userLocation={userLocation}
        />
      )}
    </main>
  )
}
